'use client';

import { Loader2 } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { priceTargetCents } from '@/lib/api/priceAlerts';
import { Link } from '@/lib/routing';
import { Icon, type IconName } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { BUTTON_M_CLASS, LABEL_CLASS } from '../constants';
import { ExpiresField } from '../create/ExpiresField';
import { NoteField } from '../create/NoteField';
import { RepeatField } from '../create/RepeatField';
import { TargetPriceField } from '../create/TargetPriceField';
import { TargetVolumeField } from '../create/TargetVolumeField';
import type {
  PriceAlert,
  PriceAlertCondition,
  PriceAlertExpires,
  PriceAlertRepeat,
} from '../types';
import { isPriceCondition, isUsdValueCondition, WHALE_DEFAULT_CENTS } from '../utils';

interface PriceAlertEditViewProps {
  alert: PriceAlert;
  onCancel: () => void;
  onSave: (payload: EditPayload) => void;
  isSubmitting?: boolean;
}

export interface EditPayload {
  targetCents?: number;
  repeat?: PriceAlertRepeat;
  expires?: PriceAlertExpires;
  note?: string;
}

const CONDITION_ICONS: Record<PriceAlertCondition, IconName> = {
  PRICE_ABOVE: 'ArrowUp',
  PRICE_BELOW: 'ArrowDown',
  VOLUME_24H_ABOVE: 'VolumeUp',
  VOLUME_24H_BELOW: 'VolumeDown',
  BOOK_DEPTH_ABOVE: 'OrderbookAll',
  BOOK_DEPTH_BELOW: 'OrderbookAll',
  WHALE_TRADE: 'Whale',
};

const CONDITION_LABEL_KEYS: Record<
  PriceAlertCondition,
  'priceAbove' | 'priceBelow' | 'volumeAbove' | 'volumeBelow' | 'oiAbove' | 'oiBelow' | 'whaleTrade'
> = {
  PRICE_ABOVE: 'priceAbove',
  PRICE_BELOW: 'priceBelow',
  VOLUME_24H_ABOVE: 'volumeAbove',
  VOLUME_24H_BELOW: 'volumeBelow',
  BOOK_DEPTH_ABOVE: 'oiAbove',
  BOOK_DEPTH_BELOW: 'oiBelow',
  WHALE_TRADE: 'whaleTrade',
};

function alertCurrentCents(alert: PriceAlert): number {
  if (alert.currentValue == null) return 0;
  if (isPriceCondition(alert.conditionType)) return priceTargetCents(alert.currentValue);
  return Math.round(alert.currentValue * 100);
}

function initialTargetCents(alert: PriceAlert): number {
  if (isPriceCondition(alert.conditionType)) return priceTargetCents(alert.targetValue);
  return Math.round(alert.targetValue * 100);
}

function expiresFromCreated(createdAtIso: string, mode: PriceAlertExpires): Date | null {
  const created = new Date(createdAtIso);
  if (mode === 'DURATION_24H') return new Date(created.getTime() + 24 * 60 * 60 * 1000);
  if (mode === 'DURATION_7D') return new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000);
  return null;
}

export function PriceAlertEditView({
  alert,
  onCancel,
  onSave,
  isSubmitting = false,
}: PriceAlertEditViewProps) {
  const t = useTranslations('priceAlerts');
  const tEdit = useTranslations('priceAlerts.edit');
  const format = useFormatter();

  const isYes = alert.side === 'YES';
  const isPrice = isPriceCondition(alert.conditionType);
  const isUsd = isUsdValueCondition(alert.conditionType);

  const initialCents = useMemo(() => initialTargetCents(alert), [alert]);

  const [targetCents, setTargetCents] = useState<number>(initialCents);
  const [repeat, setRepeat] = useState<PriceAlertRepeat>(alert.repeatMode);
  const [expires, setExpires] = useState<PriceAlertExpires>(alert.expiresMode);
  const [note, setNote] = useState<string>(alert.note ?? '');

  const currentCents = alertCurrentCents(alert);
  const currentUsd = isUsd && alert.currentValue != null ? alert.currentValue : 0;

  const isValid = isPrice ? targetCents > 0 && targetCents < 100 : targetCents > 0;

  const targetChanged = targetCents !== initialCents;
  const repeatChanged = repeat !== alert.repeatMode;
  const expiresChanged = expires !== alert.expiresMode;
  const noteChanged = (note ?? '').trim() !== (alert.note ?? '').trim();
  const hasChanges = targetChanged || repeatChanged || expiresChanged || noteChanged;

  const isTriggered = alert.status === 'TRIGGERED';
  const willRearmFromTarget = isTriggered && targetChanged;
  const willRearmFromRepeat =
    isTriggered && alert.repeatMode === 'ONCE' && repeat === 'PER_TRIGGER';
  const rearmHint = willRearmFromTarget
    ? tEdit('rearmHint')
    : willRearmFromRepeat
      ? tEdit('rearmHintSwitch')
      : null;

  const expiresPreview = useMemo(() => {
    if (!expiresChanged) return null;
    const date = expiresFromCreated(alert.createdAt, expires);
    if (!date) return null;
    return tEdit('expiresFromCreated', {
      date: format.dateTime(date, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
      }),
    });
  }, [expiresChanged, expires, alert.createdAt, format, tEdit]);

  const handleSave = () => {
    if (!isValid || !hasChanges) return;
    const payload: EditPayload = {};
    if (targetChanged) payload.targetCents = targetCents;
    if (repeatChanged) payload.repeat = repeat;
    if (expiresChanged) payload.expires = expires;
    if (noteChanged) payload.note = note.trim();
    onSave(payload);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-1 transition-opacity',
          isSubmitting && 'pointer-events-none opacity-50',
        )}
        aria-busy={isSubmitting || undefined}
      >
        <ImmutableSummary alert={alert} isYes={isYes} />

        <ConditionReadOnly condition={alert.conditionType} />

        {isPrice ? (
          <TargetPriceField
            valueCents={targetCents}
            onChangeCents={setTargetCents}
            currentCents={currentCents}
          />
        ) : (
          <TargetVolumeField
            valueCents={
              targetCents > 0
                ? targetCents
                : alert.conditionType === 'WHALE_TRADE'
                  ? WHALE_DEFAULT_CENTS
                  : 0
            }
            onChangeCents={setTargetCents}
            currentVolume={currentUsd}
            condition={alert.conditionType}
          />
        )}

        <div className="grid grid-cols-1 gap-4 min-[800px]:grid-cols-2">
          <RepeatField value={repeat} onChange={setRepeat} />
          <ExpiresField value={expires} onChange={setExpires} />
        </div>

        {expiresPreview && (
          <div className="-mt-3 flex items-start gap-1.5 rounded-[10px] bg-v2-btn-secondary px-3 py-2 text-[12px] leading-[150%] text-v2-text-secondary">
            <Icon name="Attention" size={14} className="mt-0.5 shrink-0 text-v2-text-secondary" />
            <span>{expiresPreview}</span>
          </div>
        )}

        <NoteField value={note} onChange={setNote} />

        {rearmHint && (
          <div className="flex items-start gap-1.5 rounded-[10px] bg-v2-toggle-green-surface px-3 py-2 text-[12px] leading-[150%] text-v2-green">
            <Icon name="Attention" size={14} className="mt-0.5 shrink-0" />
            <span>{rearmHint}</span>
          </div>
        )}

        {alert.triggerCount > 0 && (
          <span className={cn(LABEL_CLASS, 'text-v2-text-secondary')}>
            {tEdit('triggerCount', { count: alert.triggerCount })}
          </span>
        )}
      </div>

      <div className="flex gap-4">
        <Button
          type="button"
          appearance="neutral"
          size="M"
          className="flex-1"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </Button>
        <Button
          type="button"
          appearance="accent"
          size="M"
          className="flex-1"
          onClick={handleSave}
          disabled={!isValid || !hasChanges || isSubmitting}
          leftIcon={isSubmitting && <Loader2 className="size-4 animate-spin" />}
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
}

interface ImmutableSummaryProps {
  alert: PriceAlert;
  isYes: boolean;
}

function ImmutableSummary({ alert, isYes }: ImmutableSummaryProps) {
  const t = useTranslations('priceAlerts');

  return (
    <div className="flex flex-col gap-2">
      <h3 className={cn(BUTTON_M_CLASS, 'text-v2-text-primary')}>
        {alert.outcomeName?.trim() || alert.eventName}
      </h3>
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          className={cn(
            'shrink-0 rounded-[8px] px-2 py-0.5 text-[12px] leading-4 font-normal',
            isYes ? 'bg-v2-green-surface text-v2-green' : 'bg-v2-red-surface text-red-400',
          )}
        >
          {isYes ? t('side.yes') : t('side.no')}
        </span>
        {alert.eventSlug ? (
          <Link
            href={`/event/${alert.eventSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-[13px] leading-4 font-normal text-v2-text-secondary transition-colors hover:text-v2-text-primary hover:underline"
          >
            {alert.eventName}
          </Link>
        ) : (
          <span className="truncate text-[13px] leading-4 font-normal text-v2-text-secondary">
            {alert.eventName}
          </span>
        )}
      </div>
    </div>
  );
}

interface ConditionReadOnlyProps {
  condition: PriceAlertCondition;
}

function ConditionReadOnly({ condition }: ConditionReadOnlyProps) {
  const tForm = useTranslations('priceAlerts.form');
  const icon = CONDITION_ICONS[condition];
  const label = tForm(`conditions.${CONDITION_LABEL_KEYS[condition]}`);

  return (
    <div className="flex flex-col gap-2">
      <span className={LABEL_CLASS}>{tForm('condition')}</span>
      <div className="flex h-10 items-center gap-1.5 rounded-[10px] bg-v2-btn-secondary px-3 dark:bg-v2-surface-tertiary">
        <Icon name={icon} size={20} className="shrink-0 text-v2-text-primary" />
        <span className="truncate text-[12px] leading-[115%] font-normal text-v2-text-primary">
          {label}
        </span>
      </div>
    </div>
  );
}
